/**
 * What the browser contributes to an AI Support conversation.
 *
 * The rule: we send **identifiers and diagnostics, never records**. The envelope
 * says which patient or meeting is open, not what is in it — the assistant
 * re-fetches through the permission-checked API, so there is one path to data.
 *
 * The console buffer starts at app boot, because the errors worth reading happen
 * before anyone thinks to open the assistant. It is only ever transmitted while
 * a session is active.
 */

import { openModalId } from "./resolveTarget";

export interface ConsoleEntry {
  level: "log" | "warn" | "error" | "info";
  message: string;
  at: string;
}

export interface FailedRequest {
  method: string;
  url: string;
  status?: number;
  duration_ms?: number;
  request_id?: string;
  detail?: string;
  at: string;
}

export interface ClientContextEnvelope {
  screen?: { type?: string; path?: string; params?: string; title?: string };
  entity?: { patient_id?: string | number; meeting_id?: string | number; task_id?: string | number };
  ui_state?: { modal?: string; tab?: string; filters?: string; selection?: string };
  console?: ConsoleEntry[];
  requests?: FailedRequest[];
  app?: { version?: string; browser?: string; os?: string; viewport?: string; lang?: string };
}

const MAX_CONSOLE = 200;
const MAX_REQUESTS = 40;
const MAX_MESSAGE_CHARS = 1200;

/**
 * Strip anything token-shaped. The backend scrubs again — this is the first of
 * two passes, and the cheap one, because it happens before the value leaves the
 * browser at all.
 */
const TOKEN_PATTERNS: RegExp[] = [
  /([?&](?:token|access_token|auth|api_key|key)=)[^&\s"']+/gi,
  /("?(?:token|access_token|apiKey|api_key|authorization)"?\s*[:=]\s*"?)[A-Za-z0-9._-]{12,}/gi,
  /\bBearer\s+[A-Za-z0-9._-]{12,}/gi,
];

export const redact = (text: string): string => {
  let out = text;
  TOKEN_PATTERNS.forEach((pattern) => {
    out = out.replace(pattern, (match, prefix) => `${prefix ?? ""}[redacted]`);
  });
  return out;
};

const consoleBuffer: ConsoleEntry[] = [];
const requestBuffer: FailedRequest[] = [];

const push = <T,>(buffer: T[], entry: T, cap: number): void => {
  buffer.push(entry);
  if (buffer.length > cap) buffer.splice(0, buffer.length - cap);
};

const stringifyArg = (arg: unknown): string => {
  if (typeof arg === "string") return arg;
  if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
  try {
    return JSON.stringify(arg);
  } catch {
    return String(arg);
  }
};

export const recordConsole = (level: ConsoleEntry["level"], args: unknown[]): void => {
  const message = redact(args.map(stringifyArg).join(" ")).slice(0, MAX_MESSAGE_CHARS);
  if (!message) return;
  push(consoleBuffer, { level, message, at: new Date().toISOString() }, MAX_CONSOLE);
};

export const recordFailedRequest = (entry: Omit<FailedRequest, "at">): void => {
  push(
    requestBuffer,
    {
      ...entry,
      url: redact(entry.url || "").slice(0, 400),
      detail: entry.detail ? redact(entry.detail).slice(0, 400) : undefined,
      at: new Date().toISOString(),
    },
    MAX_REQUESTS
  );
};

let installed = false;

/**
 * Patch the console and the global error handlers. Called once from index.tsx,
 * before the app renders, so nothing is missed.
 */
export const installClientContextCapture = (): void => {
  if (installed || typeof window === "undefined") return;
  installed = true;

  (["log", "warn", "error", "info"] as const).forEach((level) => {
    const original = console[level].bind(console);
    console[level] = (...args: unknown[]) => {
      try {
        // Only keep the app's own structured logs plus everything at warn and
        // above; unfiltered `log` would be mostly third-party noise.
        if (level !== "log" || /^\[/.test(stringifyArg(args[0]))) {
          recordConsole(level, args);
        }
      } catch {
        /* capture must never break logging */
      }
      original(...args);
    };
  });

  window.addEventListener("error", (event) => {
    recordConsole("error", [event.message, event.filename ? `at ${event.filename}:${event.lineno}` : ""]);
  });
  window.addEventListener("unhandledrejection", (event) => {
    recordConsole("error", ["Unhandled promise rejection:", event.reason]);
  });
};

const browserInfo = (): ClientContextEnvelope["app"] => {
  if (typeof window === "undefined") return undefined;
  const ua = navigator.userAgent || "";
  const browser =
    /Edg\//.test(ua) ? "Edge" :
    /Chrome\//.test(ua) ? "Chrome" :
    /Firefox\//.test(ua) ? "Firefox" :
    /Safari\//.test(ua) ? "Safari" : "Other";
  const os =
    /Windows/.test(ua) ? "Windows" :
    /Mac OS X/.test(ua) ? "macOS" :
    /Android/.test(ua) ? "Android" :
    /iPhone|iPad/.test(ua) ? "iOS" :
    /Linux/.test(ua) ? "Linux" : "Other";
  return {
    version: process.env.REACT_APP_VERSION || process.env.REACT_APP_ENV || "dev",
    browser,
    os,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
  };
};

export interface ScreenSnapshot {
  type?: string;
  patientId?: string | number;
  meetingId?: string | number;
  taskId?: string | number;
  modal?: string;
  tab?: string;
}

/**
 * Build the envelope to send with a message. Reads the live URL rather than
 * trusting a cached copy, because the therapist may have navigated since the
 * conversation started.
 */
export const buildEnvelope = (snapshot: ScreenSnapshot = {}): ClientContextEnvelope => {
  const params = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
  if (params) params.delete("token");

  // The popup on screen, read from the page rather than passed in: the modals
  // tag themselves with the same component id the interface map uses, and no
  // screen has to remember to report it. It matters because a guide is asked to
  // include the step that opens a popup — unless the therapist already has it
  // open, which is exactly what this says.
  const modal = snapshot.modal || (typeof document !== "undefined" ? openModalId() : null);

  const entity: NonNullable<ClientContextEnvelope["entity"]> = {};
  const patientId = snapshot.patientId ?? params?.get("id") ?? undefined;
  if (patientId) entity.patient_id = patientId;
  if (snapshot.meetingId) entity.meeting_id = snapshot.meetingId;
  if (snapshot.taskId) entity.task_id = snapshot.taskId;

  return {
    screen: {
      type: snapshot.type,
      path: typeof window !== "undefined" ? window.location.pathname : undefined,
      params: params?.toString() || undefined,
      title: typeof document !== "undefined" ? document.title : undefined,
    },
    entity: Object.keys(entity).length ? entity : undefined,
    ui_state: modal || snapshot.tab ? { modal: modal || undefined, tab: snapshot.tab } : undefined,
    console: consoleBuffer.slice(-MAX_CONSOLE),
    requests: requestBuffer.slice(-MAX_REQUESTS),
    app: browserInfo(),
  };
};

/** Exposed for tests and for the widget's "what can you see?" affordance. */
export const peekBuffers = () => ({
  console: consoleBuffer.slice(),
  requests: requestBuffer.slice(),
});
